import React from "react";
import "./login.css";
import googleLogo from "../../assets/images/google-logo.svg";
import { useLogin } from "../../hooks/use-login";
import passwordShowIcon from "../../assets/images/passwordshow.png";
import passwordHideIcon from "../../assets/images/passwordhide.png";
import { AuthUser } from "../../types/auth-types";
import Modal from "../uiComponents/Modal";
import Toast from "../uiComponents/Toast";

interface AuthProps {
  onAuthSuccess: (user: AuthUser) => void;
}

const brandLogo = `${process.env.PUBLIC_URL}/impret-icon.svg`;

const Login: React.FC<AuthProps> = ({ onAuthSuccess }) => {
  const {
    showPassword,
    handleSignIn,
    handleGoogleSignIn,
    handleForgotPassword,
    handleCreateAccount,
    email,
    password,
    errors,
    forgotModalOpen,
    forgotEmail,
    forgotSubmitting,
    closeForgotModal,
    submitForgotPassword,
    toast,
    handleToastClose,
    handleEmailChange,
    handlePasswordChange,
    togglePasswordVisibility,
    handleForgotEmailChange,
    signInButtonState,
  } = useLogin(onAuthSuccess);

  return (
    <div className="login-container">
      <div className="login-left-panel">
        <div className="brand-logo">
          <img src={brandLogo} alt="Impret" className="brand-logo-mark" />
          Impret
        </div>
        <div className="login-hero-content">
          <h1 className="hero-title">
            Stop repeating brand context for every draft.
          </h1>
          <p className="hero-subtitle">
            Add your product context once, then plan weekly content, draft posts,
            and create SEO from the same workspace.
          </p>
        </div>
      </div>
      <div className="login-right-panel">
        <div className="login-form-wrapper">
          <div className="form-header">
            <h2 className="form-title">Welcome back</h2>
            <p className="form-subtitle">
              Enter your details to access your dashboard.
            </p>
          </div>
          <form onSubmit={handleSignIn}>
            <div className="input-group">
              <label className="input-label">Email Address</label>
              <input
                type="email"
                className={`input-field ${errors.email ? "error" : ""}`}
                placeholder="name@company.com"
                value={email}
                onChange={(e) => handleEmailChange(e.target.value)}
              />
              {errors.email && (
                <span className="error-message">{errors.email}</span>
              )}
            </div>
            <div className="input-group">
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: 8,
                }}
              >
                <label className="input-label" style={{ marginBottom: 0 }}>
                  Password
                </label>
                <span className="forgot-link" onClick={handleForgotPassword}>
                  Forgot password?
                </span>
              </div>
              <div className="input-field-wrapper">
                <input
                  type={showPassword ? "text" : "password"}
                  className={`input-field ${errors.password ? "error" : ""}`}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => handlePasswordChange(e.target.value)}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={togglePasswordVisibility}
                >
                  <img
                    src={showPassword ? passwordHideIcon : passwordShowIcon}
                    alt={showPassword ? "Hide password" : "Show password"}
                    style={{ width: 20, height: 20 }}
                  />
                </button>
              </div>
              {errors.password && (
                <span className="error-message">{errors.password}</span>
              )}
            </div>
            <button
              type="submit"
              className="login-button"
              disabled={signInButtonState.disabled}
              style={signInButtonState.style}
            >
              {signInButtonState.label}
            </button>
          </form>
          <div className="divider">
            <span>Or continue with</span>
          </div>
          <div className="social-login-row">
            <button className="social-button" onClick={handleGoogleSignIn}>
              <img
                src={googleLogo}
                alt="Google"
                style={{ width: 20, height: 20 }}
              />
              Google
            </button>
          </div>
          <p className="footer-text">
            Don't have an account?
            <span className="create-account-link" onClick={handleCreateAccount}>
              Create an account
            </span>
          </p>
        </div>
      </div>

      <Modal
        isOpen={forgotModalOpen}
        onClose={closeForgotModal}
        title="Reset your password"
        size="small"
      >
        <div style={{ display: "grid", gap: 12 }}>
          <p style={{ margin: 0, color: "#4B5563", fontSize: 14 }}>
            Enter your account email and we’ll send you a password reset link.
          </p>
          <input
            type="email"
            className="input-field"
            placeholder="name@company.com"
            value={forgotEmail}
            onChange={(e) => handleForgotEmailChange(e.target.value)}
          />
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <button
              className="social-button"
              onClick={closeForgotModal}
              disabled={forgotSubmitting}
            >
              Cancel
            </button>
            <button
              className="login-button"
              onClick={submitForgotPassword}
              disabled={forgotSubmitting}
            >
              {forgotSubmitting ? "Sending..." : "Send reset link"}
            </button>
          </div>
        </div>
      </Modal>

      {toast?.isVisible && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={handleToastClose}
          duration={3000}
        />
      )}
    </div>
  );
};

export default Login;
